import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateCargo } from 'api/cargos/use-update-cargo.ts';
import type { UpdateCargoReq } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { formatDate } from 'shared/utils/time/format-date';
import { CargoType } from 'types/cargo';
import { CargoForm } from 'components/cargos/cargo-form';
import type { ICargoFormFields } from 'components/cargos/cargo-form';
import { prepareCargoAddressPayload, prepareOptionalCargoAddressPayload } from 'components/cargos/cargo-form/utils';
import {
  FormDrawerWithButton,
  EFormDrawerSize,
  EFormDrawerWithButtonIconType,
} from 'components/common/form-drawer-with-button';
import type { ICargoEditProps } from './types.ts';
import { prepareInitialValue } from './utils.ts';

const CargoEditComponent: React.FC<ICargoEditProps> = (props) => {
  const { cargo } = props;

  const { t } = useTranslation();
  const { mutateAsync: updateCargo, isLoading } = useUpdateCargo(cargo.id);

  const handleFinish = async (values: ICargoFormFields) => {
    const loadingAddress = prepareCargoAddressPayload(values.loadingAddress);
    const unloadingAddress = prepareCargoAddressPayload(values.unloadingAddress);

    const apiVariables: UpdateCargoReq = {
      loadingDateFrom: values.loadingDate?.[0] && formatDate(values.loadingDate?.[0]),
      loadingDateTo: values.loadingDate?.[1] && formatDate(values.loadingDate?.[1]),
      unloadingDateFrom: values.unloadingDate?.[0] && formatDate(values.unloadingDate?.[0]),
      unloadingDateTo: values.unloadingDate?.[1] && formatDate(values.unloadingDate?.[1]),
      loadingCustom: values.loadingCustom,
      loadingCustomAddress: prepareOptionalCargoAddressPayload(values.loadingCustomAddress),
      unloadingCustom: values.unloadingCustom,
      unloadingCustomAddress: prepareOptionalCargoAddressPayload(values.unloadingCustomAddress),
      assetReturnAddress:
        values.type === CargoType.CONTAINER ? prepareOptionalCargoAddressPayload(values.assetReturnAddress) : undefined,
      type: values.type,
      loadingAddress: {
        ...loadingAddress,
        extraData: loadingAddress.extraData && loadingAddress.extraData?.length ? loadingAddress.extraData : null,
      },
      unloadingAddress: {
        ...unloadingAddress,
        extraData: unloadingAddress.extraData && unloadingAddress.extraData?.length ? unloadingAddress.extraData : null,
      },
      totalWeight: values.totalWeight,
    };

    await updateCargo(apiVariables);
  };

  const initialValues: ICargoFormFields = prepareInitialValue(cargo);

  return (
    <FormDrawerWithButton
      formComponent={CargoForm}
      formProps={{ initialValues, withoutItems: true }}
      buttonLabel={t('common:edit')}
      title={t('cargo:edit_cargo')}
      iconType={EFormDrawerWithButtonIconType.EDIT}
      isLoading={isLoading}
      size={EFormDrawerSize.LARGE}
      onFinish={handleFinish}
    />
  );
};

export const CargoEdit = withRoleGuard<ICargoEditProps>(CargoEditComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
]);
